import {
    Autocomplete, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItem, ListItemText,
    Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField
} from "@mui/material";
import { useAsync2, useAsyncAction } from "nate-react-api-helpers";
import { useState } from "react";
import { api } from "../../api/API";
import { AdminTeam, AdminUser } from "../../api/Admin";
import { RowMenu } from "../../misc/RowMenu";
import { useSnackAction } from "../../misc/SnackAction";
import { usePhone } from "../../misc/usePhone";

export function AdminTeams() {
    const [editing, setEditing] = useState<Partial<AdminTeam> | null>(null);
    const phone = usePhone();

    const list = useAsync2(() => api.admin.listTeams(), {}, []);

    const setArchived = useSnackAction(async (input: { team: AdminTeam; archived: boolean }) => {
        await api.admin.upsertTeam({
            id: input.team.id,
            name: input.team.name,
            archived: input.archived,
            members: input.team.members.map(member => member.usr),
        });

        list.reload();
    }, { success: "Saved" });

    return (
        <>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button variant="contained" size="small" onClick={() => setEditing({ members: [] })}>Add team</Button>
            </div>

            <div style={{ height: 16 }} />
            {list.LoadingOrErrorElement}

            {phone ? (
                <Paper>
                    <List disablePadding>
                        {list.asList.map(team => (
                            <ListItem key={team.id} divider secondaryAction={!team.personal && (
                                <RowMenu items={[
                                    { label: "Edit", onClick: () => setEditing(team) },
                                    {
                                        label: team.archived ? "Restore" : "Archive",
                                        danger: !team.archived,
                                        disabled: setArchived.loading,
                                        onClick: () => setArchived.callback({ team: team, archived: !team.archived }),
                                    },
                                ]} />
                            )}>
                                <ListItemText
                                    primary={
                                        <>
                                            {team.name}
                                            {team.personal && <Chip size="small" label="Personal" sx={{ ml: 1 }} />}
                                            {team.archived && <Chip size="small" label="Archived" sx={{ ml: 1 }} />}
                                        </>
                                    }
                                    secondary={team.members.map(member => member.name).join(", ")}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            ) : (
                <Paper>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Team</TableCell>
                                <TableCell>Members</TableCell>
                                <TableCell padding="checkbox" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {list.asList.map(team => (
                                <TableRow key={team.id}>
                                    <TableCell style={{ whiteSpace: "nowrap" }}>
                                        {team.name}
                                        {team.personal && <Chip size="small" label="Personal" sx={{ ml: 1 }} />}
                                        {team.archived && <Chip size="small" label="Archived" sx={{ ml: 1 }} />}
                                    </TableCell>
                                    <TableCell>{team.members.map(member => member.name).join(", ")}</TableCell>
                                    <TableCell padding="checkbox" style={{ whiteSpace: "nowrap" }}>
                                        {/* a personal team belongs to its user alone */}
                                        {!team.personal && (
                                            <>
                                                <Button size="small" onClick={() => setEditing(team)}>Edit</Button>
                                                <Button size="small" color={team.archived ? "primary" : "error"}
                                                        disabled={setArchived.loading}
                                                        onClick={() => setArchived.callback({ team: team, archived: !team.archived })}>
                                                    {team.archived ? "Restore" : "Archive"}
                                                </Button>
                                            </>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>
            )}

            {editing && (
                <TeamEditor
                    value={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => {
                        setEditing(null);
                        list.reload();
                    }}
                />
            )}
        </>
    );
}

// TeamEditor adds a team or renames one, and sets its members directly
function TeamEditor(props: { value: Partial<AdminTeam>; onClose: () => void; onSaved: () => void }) {
    const [name, setName] = useState(props.value.name || "");
    const [members, setMembers] = useState<number[]>((props.value.members || []).map(member => member.usr));

    const users = useAsync2(() => api.admin.listUsers(), {}, []);
    const options = users.asList.filter(user => !user.isAdmin && !user.archived);

    const save = useAsyncAction(async () => {
        await api.admin.upsertTeam({
            id: props.value.id,
            name: name,
            archived: props.value.archived || false,
            members: members,
        });

        props.onSaved();
    }, [name, members]);

    return (
        <Dialog open onClose={props.onClose} maxWidth="sm" fullWidth>
            <form onSubmit={e => {
                e.preventDefault();
                save.callback(null);
            }}>
                <DialogTitle>{props.value.id ? "Edit team" : "Add team"}</DialogTitle>
                <DialogContent style={{ display: "flex", flexDirection: "column", gap: 8, paddingTop: 8 }}>
                    <TextField label="Name" value={name} onChange={e => setName(e.target.value)} />
                    <Autocomplete
                        multiple
                        options={options}
                        value={options.filter(user => members.includes(user.id))}
                        getOptionLabel={(user: AdminUser) => `${user.firstName} ${user.lastName}`.trim() || user.email}
                        isOptionEqualToValue={(a: AdminUser, b: AdminUser) => a.id === b.id}
                        onChange={(_, value: AdminUser[]) => setMembers(value.map(user => user.id))}
                        renderInput={params => <TextField {...params} label="Members" />}
                    />
                    {users.LoadingOrErrorElement}
                    {save.LoadingElement}
                </DialogContent>
                <DialogActions>
                    <Button onClick={props.onClose}>Cancel</Button>
                    <Button type="submit" variant="contained" disabled={save.loading || !name.trim()}>Save</Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}
